import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Upload,
  Search,
  Lightbulb,
  Flag,
  ClipboardList,
  MessageSquare,
} from "lucide-react";
import { documentTypes, getDocBySlug, type DocType } from "@/data/documentTypes";

export const Route = createFileRoute("/document-types/$slug")({
  loader: ({ params }) => {
    const doc = getDocBySlug(params.slug);
    if (!doc) throw notFound();
    return { doc };
  },
  head: ({ loaderData }) => {
    const doc = loaderData?.doc;
    if (!doc) {
      return {
        meta: [{ title: "Document Type — legaldecoder" }],
      };
    }
    const title = `${doc.name} Analysis | Instant AI Review | legaldecoder`;
    const description =
      `Analyze your ${doc.name.toLowerCase()} in plain English. Spot red flags, hidden fees, and unfair clauses in under 60 seconds. Free to try.`.slice(
        0,
        155,
      );
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: `${doc.name} Analysis — Understand Your ${doc.name} in Plain English`,
      description,
      author: { "@type": "Organization", name: "legaldecoder" },
      publisher: {
        "@type": "Organization",
        name: "legaldecoder",
      },
    };
    const faqSchema =
      doc.faqs && doc.faqs.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: doc.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null;
    const url = `https://legaldecoder.io/document-types/${doc.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(articleSchema),
        },
        ...(faqSchema
          ? [{ type: "application/ld+json", children: JSON.stringify(faqSchema) }]
          : []),
      ],
    };
  },
  component: DocTypePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-3xl font-bold">Document type not found</h1>
        <p className="mt-3 text-[#6B7280]">
          We couldn't find that document type.{" "}
          <Link to="/document-types" className="text-[#1B3A6B] underline">
            Browse all 80+ document types
          </Link>
          .
        </p>
      </main>
      <Footer />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-3xl font-bold">Something went wrong</h1>
        <p className="mt-3 text-[#6B7280]">{error.message}</p>
      </main>
      <Footer />
    </div>
  ),
});

const defaultHidden: NonNullable<DocType["hidden"]> = [
  {
    emoji: "⚠️",
    title: "Hidden Auto-Renewals",
    desc: "Many contracts roll over silently — sometimes on worse terms.",
  },
  {
    emoji: "💸",
    title: "Surprise Fees",
    desc: "Look for late fees, processing fees, and admin charges buried deep.",
  },
  {
    emoji: "⛓️",
    title: "Restrictive Obligations",
    desc: "Clauses that limit your future options can outlast the contract.",
  },
  {
    emoji: "⚖️",
    title: "Forced Arbitration",
    desc: "You may be giving up your right to sue or join a class action.",
  },
  {
    emoji: "📅",
    title: "Tight Deadlines",
    desc: "Notice windows for changes or termination are often surprisingly short.",
  },
];

const defaultFaqs: NonNullable<DocType["faqs"]> = [
  {
    q: "Can legaldecoder review this document?",
    a: "Yes — upload a PDF, paste the text, or take a photo. Our AI returns red flags and a plain-English summary in under 60 seconds.",
  },
  {
    q: "Is this legally binding advice?",
    a: "No. We're an AI explainer for informational purposes only. For high-stakes decisions, consult a licensed attorney.",
  },
  {
    q: "What jurisdictions do you cover?",
    a: "Any English-speaking jurisdiction. Risk flags use widely accepted contract norms — confirm specifics with a local lawyer.",
  },
  {
    q: "How long does the analysis take?",
    a: "30–60 seconds for most documents. Long contracts may take up to two minutes.",
  },
  {
    q: "Do you store my document?",
    a: "No. Zero data retention — everything is processed in memory and discarded.",
  },
];

function DocTypePage() {
  const { doc } = Route.useLoaderData() as { doc: DocType };
  const hidden = doc.hidden ?? defaultHidden;
  const faqs = doc.faqs ?? defaultFaqs;
  const hook =
    doc.hook ??
    `Don't sign until you know what's in it. We flag unfair clauses, hidden fees, and unusual terms in your ${doc.name.toLowerCase()}.`;

  const related = (doc.related ?? [])
    .map((slug: string) => documentTypes.find((d) => d.slug === slug))
    .filter((d): d is DocType => Boolean(d));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-b from-[#EEF2FF] to-white py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <nav className="mb-6 text-xs text-[#6B7280]">
              <Link to="/document-types" className="hover:text-[#1B3A6B]">
                Document Types
              </Link>{" "}
              · <span className="text-[#374151]">{doc.category}</span>
            </nav>
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <div className="text-5xl">{doc.emoji}</div>
                <h1 className="mt-4 text-[36px] font-bold leading-tight tracking-tight text-[#111827] sm:text-[40px]">
                  {doc.name} Analysis — Understand Your {doc.name} in Plain English
                </h1>
                <p className="mt-4 text-base text-[#6B7280]">{hook}</p>
                <a
                  href="/#analyzer"
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#1B3A6B] px-6 py-4 text-base font-semibold text-white shadow-elegant transition-all hover:-translate-y-0.5 hover:bg-[#2D5A9E]"
                >
                  Analyze Your {doc.name} <ArrowRight className="h-5 w-5" />
                </a>
                <p className="mt-3 text-xs text-[#9CA3AF]">
                  No credit card required · Results in under 60 seconds
                </p>
              </div>
              <UploadCta docName={doc.name} />
            </div>
          </div>
        </section>

        {/* Problem awareness */}
        <section className="py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-[28px] font-bold text-[#111827]">
              {hidden.length} Things Your {doc.name} Might Be Hiding
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-base text-[#6B7280]">
              Most {doc.name.toLowerCase()}s contain at least one clause that's worth a closer look.
              Here's what we scan for first.
            </p>
            <ol className="mt-10 space-y-4">
              {hidden.map((item, i) => (
                <li
                  key={item.title}
                  className="flex gap-4 rounded-2xl border border-border bg-card p-6 shadow-card"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#EEF2FF] text-2xl">
                    {item.emoji}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#111827]">
                      {i + 1}. {item.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-[#6B7280]">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-[#F9FAFB] py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-[24px] font-bold text-[#111827]">How It Works</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {[
                {
                  icon: Upload,
                  emoji: "📄",
                  title: "Upload",
                  desc: "Drop your document, paste text, or snap a photo.",
                },
                {
                  icon: Search,
                  emoji: "🔍",
                  title: "Analyze",
                  desc: "AI scans your document in under 60 seconds.",
                },
                {
                  icon: Lightbulb,
                  emoji: "💡",
                  title: "Understand",
                  desc: "Get plain-English red flags and next steps.",
                },
              ].map((step, i) => (
                <div
                  key={step.title}
                  className="rounded-2xl border border-border bg-white p-6 text-center shadow-card"
                >
                  <div className="text-3xl">{step.emoji}</div>
                  <div className="mt-2 text-[11px] font-bold uppercase tracking-wider text-[#9CA3AF]">
                    Step {i + 1}
                  </div>
                  <h3 className="mt-1 text-base font-bold text-[#111827]">{step.title}</h3>
                  <p className="mt-2 text-sm text-[#6B7280]">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What you get */}
        <section className="py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-[24px] font-bold text-[#111827]">
              What You'll Receive
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {[
                {
                  Icon: Flag,
                  color: "#DC2626",
                  title: "Red Flag Detection",
                  desc: `Plain-English explanations of every risky clause specific to ${doc.name.toLowerCase()}s.`,
                },
                {
                  Icon: ClipboardList,
                  color: "#1B3A6B",
                  title: "Rights & Obligations",
                  desc: `A clear summary of what you're entitled to and what's expected of you.`,
                },
                {
                  Icon: MessageSquare,
                  color: "#7C3AED",
                  title: "Ask AI Follow-ups",
                  desc: "5 follow-up questions per analysis, answered with context from your document.",
                },
              ].map((f) => (
                <div
                  key={f.title}
                  className="rounded-2xl border border-border bg-card p-6 shadow-card"
                >
                  <f.Icon className="h-6 w-6" style={{ color: f.color }} />
                  <h3 className="mt-3 text-base font-bold text-[#111827]">{f.title}</h3>
                  <p className="mt-2 text-sm text-[#6B7280]">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="bg-[#F9FAFB] py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl border-l-4 border-[#1B3A6B] bg-white p-8 shadow-card">
              <div className="text-[#F5A623]">★★★★★</div>
              <p className="mt-3 text-base italic leading-relaxed text-[#374151]">
                "I uploaded my {doc.name.toLowerCase()} and got a clear, plain-English summary in
                under a minute. Caught two clauses I didn't fully understand and gave me the
                language to push back. Worth every cent."
              </p>
              <div className="mt-4 text-sm">
                <span className="font-bold text-[#111827]">Verified user</span>{" "}
                <span className="text-[#6B7280]">· {doc.category}</span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-[24px] font-bold text-[#111827]">
              Frequently Asked Questions
            </h2>
            <Accordion
              type="single"
              collapsible
              className="mt-8 rounded-2xl border border-border bg-card px-6 shadow-card"
            >
              {faqs.map((item) => (
                <AccordionItem key={item.q} value={item.q} className="last:border-b-0">
                  <AccordionTrigger className="py-5 text-left text-base font-semibold">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-[#6B7280]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="bg-[#F9FAFB] py-16">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <h2 className="text-center text-[20px] font-bold text-[#111827]">
                Related Document Types
              </h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((d) => (
                  <Link
                    key={d.slug}
                    to="/document-types/$slug"
                    params={{ slug: d.slug }}
                    className="group flex items-start gap-3 rounded-xl border border-border bg-white p-4 shadow-sm hover:border-[#1B3A6B] hover:shadow-md"
                  >
                    <span className="text-2xl">{d.emoji}</span>
                    <div>
                      <div className="text-sm font-semibold text-[#111827]">{d.name}</div>
                      <div className="text-xs text-[#6B7280]">{d.shortDesc}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
            <UploadCta docName={doc.name} />
            <a
              href="/#analyzer"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#1B3A6B] px-7 py-4 text-base font-semibold text-white shadow-elegant transition-all hover:-translate-y-0.5 hover:bg-[#2D5A9E]"
            >
              Analyze Your {doc.name} — Free <ArrowRight className="h-5 w-5" />
            </a>
            <p className="mt-3 text-xs text-[#9CA3AF]">
              NOT LEGAL ADVICE. FOR INFORMATIONAL PURPOSES ONLY.
            </p>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}

function UploadCta({ docName }: { docName: string }) {
  return (
    <a
      href="/#analyzer"
      className="block rounded-2xl border-2 border-dashed border-[#1B3A6B]/40 bg-white p-8 text-center transition-all hover:-translate-y-0.5 hover:border-[#1B3A6B] hover:bg-[#EEF2FF]"
    >
      <Upload className="mx-auto h-10 w-10 text-[#1B3A6B]" />
      <div className="mt-3 text-base font-semibold text-[#111827]">Drop your {docName} here</div>
      <div className="mt-1 text-sm text-[#6B7280]">
        PDF, photo, or pasted text · Analyzed in under 60 seconds
      </div>
    </a>
  );
}
